import { useCallback, useEffect, useState } from 'react';

export type ContentSize = 'small' | 'medium' | 'large';

const STORAGE_KEY = 'content_size_preference';
const MENU_COLLAPSED_KEY = 'menu_collapsed_preference';

// 尺寸配置
export const sizeConfig = {
  small: {
    '--content-font-size-base': '14px',
    '--content-font-size-title': '16px',
    '--content-font-size-subtitle': '14px',
    '--content-font-size-value': '20px',
    '--content-font-size-label': '12px',
    '--content-icon-small': '14px',
    '--content-icon-medium': '18px',
    '--content-icon-large': '24px',
    '--content-spacing-xs': '4px',
    '--content-spacing-sm': '8px',
    '--content-spacing-md': '12px',
    '--content-spacing-lg': '16px',
    '--content-spacing-xl': '24px',
    '--content-padding-card': '12px',
    '--content-padding-item': '8px',
    '--content-line-height': '20px',
    '--content-drawer-width': '480px',
    '--content-menu-item-height': '36px',
    '--content-font-size-mini': '10px',
    '--content-keyboard-btn-height': '48px',
    '--content-list-item-padding': '8px',
    '--sidebar-width-expanded': '240px',
    '--sidebar-width-collapsed': '68px',
  },
  medium: {
    '--content-font-size-base': '15px',
    '--content-font-size-title': '18px',
    '--content-font-size-subtitle': '15px',
    '--content-font-size-value': '24px',
    '--content-font-size-label': '13px',
    '--content-icon-small': '16px',
    '--content-icon-medium': '20px',
    '--content-icon-large': '28px',
    '--content-spacing-xs': '6px',
    '--content-spacing-sm': '10px',
    '--content-spacing-md': '14px',
    '--content-spacing-lg': '18px',
    '--content-spacing-xl': '28px',
    '--content-padding-card': '16px',
    '--content-padding-item': '10px',
    '--content-line-height': '22px',
    '--content-drawer-width': '560px',
    '--content-menu-item-height': '42px',
    '--content-font-size-mini': '11px',
    '--content-keyboard-btn-height': '56px',
    '--content-list-item-padding': '10px',
    '--sidebar-width-expanded': '256px',
    '--sidebar-width-collapsed': '72px',
  },
  large: {
    '--content-font-size-base': '16px',
    '--content-font-size-title': '20px',
    '--content-font-size-subtitle': '16px',
    '--content-font-size-value': '28px',
    '--content-font-size-label': '14px',
    '--content-icon-small': '18px',
    '--content-icon-medium': '24px',
    '--content-icon-large': '32px',
    '--content-spacing-xs': '8px',
    '--content-spacing-sm': '12px',
    '--content-spacing-md': '16px',
    '--content-spacing-lg': '20px',
    '--content-spacing-xl': '32px',
    '--content-padding-card': '20px',
    '--content-padding-item': '12px',
    '--content-line-height': '24px',
    '--content-drawer-width': '640px',
    '--content-menu-item-height': '48px',
    '--content-font-size-mini': '12px',
    '--content-keyboard-btn-height': '64px',
    '--content-list-item-padding': '12px',
    '--sidebar-width-expanded': '280px',
    '--sidebar-width-collapsed': '80px',
  },
};

// 同页面内容大小变更事件名
const CONTENT_SIZE_CHANGE_EVENT = 'content_size_change';

// 应用 CSS 变量到文档根元素
const applyCssVariables = (size: ContentSize) => {
  const config = sizeConfig[size];
  const root = document.documentElement;
  Object.entries(config).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

// 获取存储的尺寸设置
const getStoredSize = (): ContentSize => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'small' || stored === 'medium' || stored === 'large') {
      return stored;
    }
  } catch {
    // 忽略存储错误
  }
  return 'small';
};

// 存储尺寸设置并派发同页面事件
const storeSize = (size: ContentSize) => {
  try {
    localStorage.setItem(STORAGE_KEY, size);
  } catch {
    // 忽略存储错误
  }
  // 派发自定义事件，同页面其他组件可监听
  window.dispatchEvent(
    new CustomEvent(CONTENT_SIZE_CHANGE_EVENT, { detail: size }),
  );
};

// 获取存储的菜单收起状态
const getStoredMenuCollapsed = (): boolean => {
  try {
    const stored = localStorage.getItem(MENU_COLLAPSED_KEY);
    if (stored === 'true' || stored === 'false') {
      return stored === 'true';
    }
  } catch {
    // 忽略存储错误
  }
  return false; // 默认展开
};

// 存储菜单收起状态
const storeMenuCollapsed = (collapsed: boolean) => {
  try {
    localStorage.setItem(MENU_COLLAPSED_KEY, String(collapsed));
  } catch {
    // 忽略存储错误
  }
};

// Hook for using content size
export const useContentSize = () => {
  const [size, setSizeState] = useState<ContentSize>(getStoredSize());

  // 初始化时应用 CSS 变量
  useEffect(() => {
    applyCssVariables(size);
  }, [size]);

  const setSize = useCallback((newSize: ContentSize) => {
    setSizeState(newSize);
    storeSize(newSize);
    applyCssVariables(newSize);
  }, []);

  // 监听同页面内容大小变更事件
  useEffect(() => {
    const handleSizeChange = (e: Event) => {
      const newSize = (e as CustomEvent).detail as ContentSize;
      if (newSize && newSize !== size) {
        setSizeState(newSize);
        applyCssVariables(newSize);
      }
    };
    window.addEventListener(CONTENT_SIZE_CHANGE_EVENT, handleSizeChange);
    return () =>
      window.removeEventListener(CONTENT_SIZE_CHANGE_EVENT, handleSizeChange);
  }, [size]);

  // 监听跨标签页存储变化
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        if (
          e.newValue === 'small' ||
          e.newValue === 'medium' ||
          e.newValue === 'large'
        ) {
          setSizeState(e.newValue);
          applyCssVariables(e.newValue);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return {
    size,
    setSize,
  };
};

// Hook for using menu collapsed preference
export const useMenuCollapsedPreference = () => {
  const [collapsed, setCollapsedState] = useState<boolean>(
    getStoredMenuCollapsed(),
  );

  const setCollapsed = useCallback((newCollapsed: boolean) => {
    setCollapsedState(newCollapsed);
    storeMenuCollapsed(newCollapsed);
  }, []);

  // 监听跨标签页存储变化
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === MENU_COLLAPSED_KEY && e.newValue !== null) {
        setCollapsedState(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return {
    collapsed,
    setCollapsed,
  };
};

// 全局状态（用于非React上下文）
let globalSize: ContentSize = getStoredSize();

export const getGlobalContentSize = (): ContentSize => globalSize;

export const setGlobalContentSize = (size: ContentSize) => {
  globalSize = size;
  storeSize(size);
  applyCssVariables(size);
};
